import { useState, useRef, useCallback, useEffect } from 'react'
import { ShieldCheck, AlertCircle, Loader2, KeyRound } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useMfa } from '@/features/auth/hooks/use-mfa'
import { markMfaVerified } from '@/features/auth/hooks/use-mfa-session'

interface MfaChallengeProps {
  factorId: string
  onSuccess: () => void
  onCancel?: () => void
}

export function MfaChallenge({ factorId, onSuccess, onCancel }: MfaChallengeProps) {
  const { challenge, verifyBackupCode, loading, error, clearError } = useMfa()

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [useBackup, setUseBackup] = useState(false)
  const [backupCode, setBackupCode] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Focus first input on mount
  useEffect(() => {
    if (!useBackup) {
      inputRefs.current[0]?.focus()
    }
  }, [useBackup])

  const handleVerifyTotp = useCallback(
    async (code?: string) => {
      const totpCode = code ?? digits.join('')
      if (totpCode.length !== 6) return

      clearError()
      const success = await challenge(factorId, totpCode)
      if (success) {
        // Record MFA session verification timestamp
        markMfaVerified()
        if (rememberDevice) {
          // Store device trust in localStorage (30-day expiry)
          try {
            const trust = {
              factorId,
              expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
            }
            localStorage.setItem('zero_mfa_trusted_device', JSON.stringify(trust))
          } catch {
            // localStorage may not be available
          }
        }
        onSuccess()
      } else {
        // Reset digits on failure
        setDigits(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      }
    },
    [digits, clearError, challenge, factorId, rememberDevice, onSuccess],
  )

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      const digit = value.replace(/\D/g, '').slice(-1)
      const newDigits = [...digits]
      newDigits[index] = digit
      setDigits(newDigits)
      clearError()

      // Auto-advance to next input
      if (digit && index < 5) {
        inputRefs.current[index + 1]?.focus()
      }

      // Auto-submit when all 6 digits are filled
      if (digit && index === 5) {
        const code = [...newDigits.slice(0, 5), digit].join('')
        if (code.length === 6) {
          handleVerifyTotp(code)
        }
      }
    },
    [digits, clearError, handleVerifyTotp],
  )

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    },
    [digits],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault()
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
      const newDigits = [...digits]
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] ?? ''
      }
      setDigits(newDigits)

      // Focus the next empty input or the last one
      const nextEmpty = newDigits.findIndex((d) => !d)
      if (nextEmpty >= 0) {
        inputRefs.current[nextEmpty]?.focus()
      } else {
        inputRefs.current[5]?.focus()
        // Auto-submit
        if (pasted.length === 6) {
          handleVerifyTotp(pasted)
        }
      }
    },
    [digits, handleVerifyTotp],
  )

  const handleVerifyBackup = async () => {
    if (!backupCode.trim()) return
    clearError()
    const success = await verifyBackupCode(backupCode.trim())
    if (success) {
      // Record MFA session verification timestamp
      markMfaVerified()
      onSuccess()
    }
  }

  return (
    <Card className="mx-auto max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-center">
          <ShieldCheck className="h-5 w-5 text-zero-600" />
          Two-Factor Authentication
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {!useBackup ? (
          <div className="space-y-4">
            <p className="text-center text-sm text-gray-600">
              Enter the 6-digit code from your authenticator app.
            </p>

            {/* 6-digit code input with auto-advance */}
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  aria-label={`Digit ${index + 1}`}
                  className="h-12 w-10 rounded-lg border border-gray-300 text-center text-xl font-mono font-bold text-gray-900 focus:border-zero-500 focus:outline-none focus:ring-2 focus:ring-zero-500/20 sm:h-14 sm:w-12"
                  disabled={loading}
                />
              ))}
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Verifying...
              </div>
            )}

            {/* Remember device */}
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-zero-600 focus:ring-zero-500"
              />
              Remember this device for 30 days
            </label>

            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                onClick={() => handleVerifyTotp()}
                loading={loading}
                disabled={digits.join('').length !== 6}
                className="w-full"
              >
                Verify
              </Button>

              {onCancel && (
                <Button variant="outline" onClick={onCancel} className="w-full">
                  Cancel
                </Button>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setUseBackup(true)
                  clearError()
                }}
                className="text-sm text-zero-600 hover:text-zero-700 hover:underline"
              >
                <KeyRound className="mr-1 inline h-3.5 w-3.5" />
                Use a backup code
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-center text-sm text-gray-600">
              Enter one of your backup codes to sign in.
            </p>

            <Input
              label="Backup Code"
              placeholder="XXXX-XXXX"
              value={backupCode}
              onChange={(e) => {
                setBackupCode(e.target.value)
                clearError()
              }}
              className="text-center font-mono text-lg tracking-wider"
              autoFocus
            />

            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                onClick={handleVerifyBackup}
                loading={loading}
                disabled={!backupCode.trim()}
                className="w-full"
              >
                Verify Backup Code
              </Button>

              {onCancel && (
                <Button variant="outline" onClick={onCancel} className="w-full">
                  Cancel
                </Button>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setUseBackup(false)
                  clearError()
                  setBackupCode('')
                }}
                className="text-sm text-zero-600 hover:text-zero-700 hover:underline"
              >
                Use authenticator app instead
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
